import { ChangeDetectionStrategy, Component, Injector, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { BusinessAreaService } from '../business-area.service';
import { IBusinessAreaModel } from '../business-area.model';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { UserService } from '@fi-app/user/user.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiBusinessAreaListHelperService } from './fi-business-area-list-helper.service';

@Component({
    selector: 'fi-business-area-list',
    templateUrl: './business-area-list.component.html',
    styleUrls: ['./business-area-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-business-area-list' },
    standalone: false,
})
export class BusinessAreaListComponent extends FiRemoteStateFullDataSource<IBusinessAreaModel> implements OnInit {
    private businessAreaService = inject(BusinessAreaService);
    private statisticService = inject(StatisticService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private userService = inject(UserService);
    private fiBusinessAreaListHelperService = inject(FiBusinessAreaListHelperService);

    protected readonly businessAreaAccessRight = this.stateAccessCheckService.isStateAllowedForCurrentUser(
        FiStateNames.BusinessArea.Add,
    );

    public defaultSortColumnName = 'Name';
    public itemsListType = FiItemsListType.BusinessAreaList;
    public defaultSortDirection = false;
    public objectType = ObjectTypes.BusinessArea;
    protected itemsListActions: ReadonlyArray<IItemsListAction2> =
        this.fiBusinessAreaListHelperService.getItemsListActions(this.confirmDeleteSelectedBusinessAreas.bind(this));
    protected listItemActions: ReadonlyArray<IItemsListAction2> =
        this.fiBusinessAreaListHelperService.getListItemActions();
    protected readonly FiStateNames = FiStateNames;

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.statisticService.Visit(ModuleType.Core);
    }

    private confirmDeleteSelectedBusinessAreas(): void {
        this.fiItemsDeleteHelperService
            .commonDeleteConfirm(ObjectTypes.BusinessArea, this.selectedItems())
            .then(() => this.deleteSelectedBusinessAreas())
            .catch(() => {});
    }

    private deleteSelectedBusinessAreas(): void {
        const selectedItems = this.selectedItems();
        const selectedItemIds = selectedItems.map((selectedItem) => selectedItem.Id);
        this.businessAreaService.DeleteBusinessAreas(selectedItemIds).then(() => {
            this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                ObjectTypes.BusinessArea,
                selectedItemIds,
                selectedItems[0].Name,
            );
            this.reloadData();
        });
    }

    protected openUserViewDialog($event: MouseEvent, userId: number): void {
        $event.stopPropagation();
        this.userService.openUserViewDialog(userId);
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);

        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return this.businessAreaService.getBusinessAreasListData(parameter);
    }
}
